import { useState } from "react";
import { Check, Calendar, Rocket } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Analytics } from "@/lib/analytics";
import { signupUrl } from "@/lib/productApp";
import CalendlyModal from "@/components/CalendlyModal";
import type { AdsMode } from "./AdsModeSelector";

const modeLabels = {
  vente: { fiches: "Fiches prospects qualifiées", starterDesc: "Pour les agences de transaction" },
  location: { fiches: "Fiches candidats qualifiées", starterDesc: "Pour les gestionnaires locatifs" },
};

const getPlans = (mode: AdsMode) => [
  {
    name: "Starter",
    price: 189,
    description: modeLabels[mode].starterDesc,
    features: [
      "5 utilisateurs",
      "1 numéro de téléphone",
      "5 contacts de notification",
      "Alertes e-mail",
      modeLabels[mode].fiches,
    ],
  },
  {
    name: "Pro",
    price: 249,
    popular: true,
    description: "Pour les agences en croissance",
    features: [
      "10 utilisateurs",
      "1 numéro de téléphone",
      "10 contacts de notification",
      "Alertes e-mail + SMS",
      modeLabels[mode].fiches,
      "Statistiques avancées",
    ],
  },
  {
    name: "Entreprise",
    price: 549,
    description: "Pour les réseaux multi-agences",
    features: [
      "50 utilisateurs",
      "10 numéros de téléphone",
      "20 contacts de notification",
      "Alertes e-mail + SMS",
      modeLabels[mode].fiches,
      "Statistiques avancées",
      "Accompagnement dédié",
    ],
  },
];

const subtitles = {
  vente: "Un seul mandat récupéré rembourse Immovox pour l'année.",
  location: "Quelques biens loués plus vite rentabilisent l'abonnement.",
};

const AdsPricing = ({ mode }: { mode: AdsMode }) => {
  const [calendlyOpen, setCalendlyOpen] = useState(false);

  return (
    <>
      <section className="section-padding bg-background">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center mb-12">
            <span className="inline-block px-4 py-1.5 rounded-full bg-primary/10 text-primary text-sm font-medium mb-4">
              À partir de 189 €/mois
            </span>
            <h2 className="text-3xl md:text-4xl font-semibold text-foreground">
              Combien ça coûte ?
            </h2>
            <p className="text-muted-foreground mt-3">
              {subtitles[mode]}
            </p>
            <p className="text-muted-foreground text-sm mt-1">
              Sans engagement · Prix HT
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {getPlans(mode).map((plan) => (
              <div
                key={plan.name}
                className={`card-stripe relative flex flex-col p-6 md:p-8 ${
                  plan.popular
                    ? "border-primary/30 ring-2 ring-primary/20 shadow-warm-lg"
                    : ""
                }`}
              >
                {plan.popular && (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-0.5 rounded-full bg-primary text-primary-foreground text-xs font-semibold">
                    Le plus populaire
                  </span>
                )}

                <h3 className="text-lg font-bold text-foreground">{plan.name}</h3>
                <p className="text-sm text-muted-foreground mt-1">{plan.description}</p>

                <div className="mt-5 mb-6">
                  <span className="text-4xl font-bold text-foreground">{plan.price}€</span>
                  <span className="text-muted-foreground text-sm">/mois HT</span>
                </div>

                <ul className="space-y-2.5 flex-1 mb-8">
                  {plan.features.map((feature) => (
                    <li key={feature} className="flex items-start gap-2 text-sm text-foreground">
                      <Check className="w-4 h-4 text-primary shrink-0 mt-0.5" />
                      {feature}
                    </li>
                  ))}
                </ul>

                <Button
                  className={`w-full rounded-full ${
                    plan.popular
                      ? "bg-primary text-primary-foreground hover:bg-primary/90"
                      : "bg-primary/10 text-primary hover:bg-primary/20"
                  }`}
                  asChild
                >
                  <a
                    href={signupUrl()}
                    onClick={() => Analytics.ctaClicked("pricing_signup", `ads_pricing_${plan.name.toLowerCase()}`)}
                  >
                    <Rocket className="w-4 h-4 mr-1" />
                    {plan.popular ? "Essai gratuit 7 jours" : "Démarrer mon essai"}
                  </a>
                </Button>
                <button
                  className="block w-full text-center text-xs text-muted-foreground hover:text-primary transition-colors mt-2"
                  onClick={() => {
                    Analytics.ctaClicked("pricing_demo", `ads_pricing_${plan.name.toLowerCase()}`);
                    setCalendlyOpen(true);
                  }}
                >
                  <Calendar className="w-3.5 h-3.5 inline mr-1" />
                  Ou réserver une démo
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>
      <CalendlyModal open={calendlyOpen} onOpenChange={setCalendlyOpen} source="ads_pricing_business" />
    </>
  );
};

export default AdsPricing;
